// src/TestGate.js
//
// Lightweight gate for the TEST deployment only (test.kbquick.com /
// dd-quick-test). On PROD (dd-quick-platform) this is a no-op passthrough —
// no gate, no noindex.
//
// Purpose: keep the often-out-of-date test site out of public view and out of
// Google's index during development. This is NOT real security — the passphrase
// ships in the client bundle (it's a REACT_APP_* var). It only deters casual
// visitors and crawlers, which is the stated goal.
//
// Keyed on REACT_APP_FIREBASE_PROJECT_ID (root .env.test vs .env.production),
// so it can never accidentally gate production.
import React, { useEffect, useState } from "react";
import { Box, Button, Paper, TextField, Typography } from "@mui/material";

const IS_TEST = process.env.REACT_APP_FIREBASE_PROJECT_ID === "dd-quick-test";
const PASSPHRASE = process.env.REACT_APP_TEST_GATE_PASSPHRASE || "";
const UNLOCK_KEY = "kbq_test_unlocked";

function TestGate({ children }) {
  // Fail open if not test, or if no passphrase was configured (avoids ever
  // locking ourselves out of test when the env var is missing).
  const [unlocked, setUnlocked] = useState(
    () =>
      !IS_TEST || !PASSPHRASE || localStorage.getItem(UNLOCK_KEY) === "1",
  );
  const [entry, setEntry] = useState("");
  const [error, setError] = useState(false);

  // Keep the test site out of search indexes.
  useEffect(() => {
    if (!IS_TEST) return undefined;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  if (unlocked) return children;

  const submit = (e) => {
    e.preventDefault();
    if (entry === PASSPHRASE) {
      localStorage.setItem(UNLOCK_KEY, "1");
      setUnlocked(true);
    } else {
      setError(true);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f5f5",
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 360, width: "100%", textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          KB Quick — Test Environment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This is a private test site. Enter the passphrase to continue.
        </Typography>
        <form onSubmit={submit}>
          <TextField
            type="password"
            label="Passphrase"
            fullWidth
            autoFocus
            value={entry}
            onChange={(e) => {
              setEntry(e.target.value);
              setError(false);
            }}
            error={error}
            helperText={error ? "Incorrect passphrase." : " "}
          />
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 1 }}>
            Enter
          </Button>
        </form>
      </Paper>
    </Box>
  );
}

export default TestGate;
